import { quoteMoney } from "./money.ts";
import type { Place } from "./page-location.ts";
import type { RideProduct, RideQuote, TravelMode } from "./rider-api.ts";

/**
 * The two screens before a ride request exists, away from how they are fetched.
 *
 * `1 Where to` and `2 Choose` are the only screens of the nine that EXP-002 does not serve, and that
 * is a decision taken in
 * [ADR 0026](../../../docs/adr/0026-a-layer-with-no-subject-is-read-through-one-that-has.md)
 * section 1 rather than an omission: the class chooser was considered for the BFF and rejected,
 * because the only way to get a price is a write that mints an immutable priced resource, and a BFF
 * issuing three of them to draw one screen would be deciding how many quotes a tenant holds.
 *
 * So the application does it, and the uncomfortable half of that is stated here rather than buried:
 * **drawing `2 Choose` makes three writes.** `POST /v1/ride-quotes` is the only operation on this
 * platform that produces a price, there is no read-only equivalent, and the design draws a price
 * against each of three classes before the rider has chosen one. Two things keep that bounded:
 *
 *   - the key. MOB-02 requires an `Idempotency-Key` and this application derives one from the route
 *     and the product rather than minting a fresh uuid, so re-rendering the screen, rotating the
 *     phone or coming back from the quote screen re-asks for the **same** quote instead of minting
 *     another. A random key per render would have made a tenant's quote count a function of how
 *     often somebody looked at a screen;
 *   - the trigger. Quotes are asked for once per route, not per keystroke, and never on `1 Where
 *     to`, which has no destination yet.
 *
 * It is recorded as a design question rather than decided silently, because the alternative reading —
 * draw the rows with no prices until a class is chosen — is defensible and loses a designed element.
 */

/* ------------------------------------------------------------------------------- product drawing */

/**
 * The glyph for a product row, from the one field that can decide it.
 *
 * The design hard-codes three glyphs against three sample products — `local_taxi` for Standard,
 * `airport_shuttle` for XL, `accessible` for Assist — and **nothing MOB-01 publishes says which
 * glyph a product has.** `vehicleClass` is a free string, `displayName` is prose, and
 * `supportedRiderCapabilities` is a list of capability names rather than an icon.
 *
 * So the glyph is chosen from `travelMode`, which is the one enumerated field about what a product
 * is, using `CLAUDE.md` section 3.5's own vocabulary — `local_taxi` for cars and `two_wheeler` for
 * motorcycles — extended to the two modes that rule does not name. A tenant whose catalogue is three
 * car products therefore draws three `local_taxi` rows where the design draws three different
 * glyphs, which is a real and visible difference and is reported as one rather than closed by
 * matching on a display name. Recorded as a design question.
 */
export function productGlyph(mode: TravelMode): string {
  switch (mode) {
    case "car":
      return "local_taxi";
    case "motorcycle":
      return "two_wheeler";
    case "bicycle":
      return "pedal_bike";
    case "walking":
      return "directions_walk";
  }
}

/**
 * `4 seats · 2 bags`, and `Wheelchair space · helper` when the product says so.
 *
 * Both halves are published: `passengerCapacity` and `luggageCapacity` are numbers on MOB-01's
 * product, and `supportedRiderCapabilities` is a list of names. The design's third row replaces the
 * capacity line entirely with its capabilities, so a product with capabilities draws them after the
 * capacity rather than instead of it — the capacity is still true and dropping it would be removing
 * something the service published.
 *
 * Singular and plural are handled because `1 seats` is the kind of detail that makes an application
 * look unfinished, and the design writes neither case for a capacity of one.
 */
export function productAttributes(product: RideProduct): string {
  const seats = `${String(product.passengerCapacity)} ${product.passengerCapacity === 1 ? "seat" : "seats"}`;
  const bags = `${String(product.luggageCapacity)} ${product.luggageCapacity === 1 ? "bag" : "bags"}`;
  const capabilities = (product.supportedRiderCapabilities ?? []).filter(
    (capability) => capability.length > 0,
  );
  return [seats, bags, ...capabilities].join(" · ");
}

/**
 * The idempotency key for one product's price on one route.
 *
 * Derived rather than random, which is the whole reason this function exists. MOB-02's `POST` mints
 * an immutable priced resource and requires a key; a fresh uuid per render would mint a new quote
 * every time the screen was drawn, so a rider who opened the chooser four times would hold twelve
 * quotes. Deriving it from the product and the two coordinates means the same screen asks for the
 * same quote, and a different route asks for a different one.
 *
 * Document 06: reusing a key with materially different terms is **refused** rather than answered
 * from the first request. That is what makes this safe rather than clever — if this derivation ever
 * collided across genuinely different terms, the service refuses with a conflict and the screen says
 * so, rather than drawing somebody else's price.
 *
 * The coordinates are written to six decimal places, which is about a tenth of a metre: finer than
 * that and the same tap produces two keys because the device's fix jittered.
 */
export function quoteKey(input: {
  readonly customerId: string;
  readonly productId: string;
  readonly origin: Place;
  readonly destination: Place;
  /** Injectable so the window below can be tested; defaults to the device's clock. */
  readonly now?: number;
}): string {
  const point = (place: Place): string =>
    `${place.latitude.toFixed(6)},${place.longitude.toFixed(6)}`;
  /*
   * Hashed rather than spelled out, because MOB-02 caps the header at 128 characters and the
   * spelled-out form was already 124 of them: two UUIDs, two coordinate pairs at six decimal
   * places and the separators. Adding the window made it 132 and every quote was refused with
   * `idempotency_key_required` — measured on the deployed estate, where both classes went from
   * one price and one gap to two gaps.
   *
   * A collision would mean two different routes asking under one key, and Document 06 makes that
   * a **refusal** rather than a wrong answer: a key reused with materially different terms is
   * rejected and the screen says so. So the failure mode of a hash here is a visible conflict,
   * never somebody else's price — which is what makes 64 bits comfortable for a handful of quotes
   * per rider.
   */
  const route = [
    input.customerId,
    input.productId,
    point(input.origin),
    point(input.destination),
  ].join("|");
  const window = Math.floor((input.now ?? Date.now()) / QUOTE_WINDOW_MS);
  return `ride-quote:${fnv1a64(route)}:${window.toString(36)}`;
}

/**
 * FNV-1a, 64-bit, in base 36.
 *
 * Not a cryptographic hash and does not need to be: nothing here is a secret, and the only
 * property required is that two different routes are overwhelmingly unlikely to produce one key.
 * `BigInt` because the 64-bit multiply overflows a double and the 32-bit variant collides far too
 * readily for comfort at this length.
 */
function fnv1a64(value: string): string {
  const prime = 1_099_511_628_211n;
  const mask = 0xffff_ffff_ffff_ffffn;
  let hash = 14_695_981_039_346_656_037n;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash ^ BigInt(value.charCodeAt(index))) * prime) & mask;
  }
  return hash.toString(36);
}

/**
 * The window a derived key is good for, and why a key needs one at all.
 *
 * **Without this a rider could never book a route twice.** The derivation above is deliberately
 * stable — the same screen must ask for the same quote, or opening the chooser four times would
 * mint twelve prices — but a key with no time in it is immortal while the quote it names is not.
 * MOB-02 answers a repeated key from its idempotency store, so once the first quote expired the
 * rider got that same dead quote back for ever, and `Get a new price` fetched the same corpse.
 * Measured on the deployed estate: three bookings on three separate runs all replayed one quote
 * minted an hour earlier, and every one of them said *"That price has expired"*.
 *
 * Five minutes, matching what the expired screen tells the rider their price is held for and what
 * MOB-02 now holds it for. So within a window the key is stable and nothing is over-minted, and
 * the moment the promise has run out the next ask is a genuinely new one.
 *
 * A boundary can fall shortly after a quote is minted, which mints a second one early. That is the
 * cheap direction to be wrong in: a spare quote costs a row, and the alternative cost a rider the
 * booking.
 */
const QUOTE_WINDOW_MS = 5 * 60 * 1000;

/**
 * `24 km · about 38 min` — the design's sub-line on `2 Choose`, from GEO-04's own two numbers.
 *
 * "about", because it is an estimate and the design says so. The duration is rounded to the nearest
 * minute and never below one.
 */
export function routeSummary(distanceMetres: number, durationSeconds: number): string {
  const km = distanceMetres / 1000;
  const distance = km < 10 ? `${km.toFixed(1)} km` : `${String(Math.round(km))} km`;
  const minutes = Math.max(1, Math.round(durationSeconds / 60));
  return `${distance} · about ${String(minutes)} min`;
}

/**
 * `Westlands → JKIA Terminal 1A`, from two reverse geocodes.
 *
 * The arrow is the design's own glyph — U+2192, not `->`. Either half may be absent, because GEO-02
 * is a service like any other and a coordinate with no answer is not a place with no name: it is a
 * place this application could not name, and the two read differently.
 */
export function routeLabel(origin: string | null, destination: string | null): string | null {
  if (origin === null && destination === null) return null;
  return `${origin ?? "Where you are"} → ${destination ?? "your destination"}`;
}

/** One product row of `2 Choose`, assembled from the two services that each hold half of it. */
export interface ProductRow {
  readonly productId: string;
  readonly glyph: string;
  readonly name: string;
  readonly attributes: string;
  /** The price, or null while MOB-02 has not answered for this product. */
  readonly price: string | null;
  /** Why there is no price, when there is none. */
  readonly priceBecause: string;
  /**
   * `3 min` — how long until a driver of that class could reach the rider. **Always null.**
   *
   * The measurement names this as the one element of `2 Choose` with no source: nothing publishes
   * `result.pickupEtaSeconds`, `products.pickupEtaSeconds` or `availability.etaSeconds`, and GEO-04
   * answers the distance between two points rather than the distance to the nearest free car of a
   * class. A number here would be a rider deciding which class to pay for on a figure nobody
   * computed.
   */
  readonly pickupEta: null;
  readonly selected: boolean;
}

/**
 * The rows of `2 Choose`, from MOB-01's catalogue and whatever MOB-02 has priced so far.
 *
 * Retired and draft products are dropped: `lifecycle` is published precisely so a catalogue can hold
 * a product nobody may book, and a rider offered one would be offered a price MOB-02 refuses to
 * mint. That filter is the one place this function removes anything the service answered, and it is
 * removing something the service marked as not for sale rather than something it published for a
 * rider.
 */
export function productRows(input: {
  readonly products: readonly RideProduct[];
  readonly quotes: ReadonlyMap<string, RideQuote>;
  readonly failed: ReadonlySet<string>;
  readonly selectedProductId: string | undefined;
}): readonly ProductRow[] {
  return input.products
    .filter((product) => product.lifecycle === "active")
    .map((product) => {
      const quote = input.quotes.get(product.productId);
      return {
        productId: product.productId,
        glyph: productGlyph(product.travelMode),
        name: product.displayName,
        attributes: productAttributes(product),
        price: quote === undefined ? null : quoteMoney(quote.fare),
        priceBecause: input.failed.has(product.productId)
          ? "No price came back"
          : "Pricing this class",
        pickupEta: null,
        selected: product.productId === input.selectedProductId,
      };
    });
}

/**
 * Which product a rider has chosen, defaulting to none rather than to the first row.
 *
 * The design draws XL selected, which is the design's own sample state rather than a rule. Choosing
 * one here on the rider's behalf would put a 2px ring round a class they did not pick and send
 * `Continue with XL` to a quote they did not ask for.
 */
export function selectedQuote(
  quotes: ReadonlyMap<string, RideQuote>,
  selectedProductId: string | undefined,
): RideQuote | undefined {
  return selectedProductId === undefined ? undefined : quotes.get(selectedProductId);
}
